import { useRef, useState, type ChangeEvent } from 'react';
import { TextField } from '@mui/material';

interface Props {
  value: number;
  onChange: (next: number) => void;
  onBlur: () => void;
  name: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
}

function rawFromValue(value: number): string {
  return value > 0 ? String(value) : '';
}

export const FlatAmountInput = ({
  value,
  onChange,
  onBlur,
  name,
  placeholder,
  error,
  helperText,
}: Props) => {
  const [rawValue, setRawValue] = useState<string>(rawFromValue(value));

  // Re-sync the local string buffer when the underlying numeric value changes
  // externally (e.g. form reset). Done during render to avoid an effect; we
  // only overwrite when the buffer no longer matches the incoming value so
  // typing isn't clobbered.
  const prevExternalValue = useRef(value);
  if (prevExternalValue.current !== value) {
    prevExternalValue.current = value;
    if (Number(rawValue) !== value) {
      setRawValue(rawFromValue(value));
    }
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextRaw = event.target.value;
    if (nextRaw !== '' && !/^\d+$/.test(nextRaw)) {
      return;
    }
    setRawValue(nextRaw);
    onChange(nextRaw === '' ? 0 : Number(nextRaw));
  };

  return (
    <TextField
      name={name}
      value={rawValue}
      onChange={handleChange}
      onBlur={onBlur}
      type="text"
      label="Flat fee amount"
      placeholder={placeholder}
      required
      inputProps={{ inputMode: 'numeric' }}
      error={error}
      helperText={helperText}
      fullWidth
    />
  );
};
